/**
 * Hidden autoplay `<audio>` sink shared by both voice transports — the
 * WebSocket path (useAudioPlayback.ts's `ensurePlaybackChain`) and the
 * [rtc-spike] WebRTC path (useCallMode.ts's rtc branch). Same element,
 * same attributes, same rationale, so the recipe lives in one place.
 *
 * Why a real <audio> element instead of raw Web Audio / WebRTC output
 * (FB29): the browser's acoustic echo canceller subtracts a REFERENCE copy
 * of what the page is playing from the mic signal — but it only taps
 * certain playback paths for that reference. Media elements are always
 * covered; raw Web Audio output is NOT on iOS WebKit (every iPhone
 * browser), so Sara's voice out of the phone speaker came back through the
 * phone mic uncancelled: she transcribed herself as the user and barged
 * herself in on every reply. Routing the final hop through an <audio>
 * element puts her voice inside the AEC reference on every platform.
 * Bonus: media-element audio also ignores the iPhone ring/silent switch,
 * which mutes raw Web Audio output entirely.
 *
 * `playsinline` keeps iOS from hijacking the page into a fullscreen
 * video-style player. The element is hidden (`display: none`) but stays
 * attached to the DOM — detached elements are flaky about MediaStream
 * playback on WebKit.
 *
 * Autoplay policy requires a user gesture to start playing, so callers
 * must create this INSIDE one (the call-start click) — a chain built or
 * played after the gesture has lapsed can come up silently autoplay-
 * blocked. `play()` is kicked off here so callers don't have to remember
 * to; a rejection is logged loudly because the user-visible symptom (call
 * connects, Sara mute) otherwise leaves no trace.
 *
 * Pass `stream` when the source already exists at creation time (the WS
 * path's `MediaStreamAudioDestinationNode`) so `srcObject` is assigned
 * BEFORE the internal `play()` — the element then never fires a play()
 * against an empty source. Omit it when the source arrives later/async
 * (the rtc path's remote track, assigned in `onRemoteStream`); `autoplay`
 * picks the stream up on assignment.
 */
export function createHiddenAudioSink(
  warnLabel: string,
  stream?: MediaStream
): HTMLAudioElement {
  const sink = document.createElement('audio');
  sink.autoplay = true;
  sink.setAttribute('playsinline', '');
  sink.style.display = 'none';
  if (stream) {
    sink.srcObject = stream;
  }
  document.body.appendChild(sink);
  sink.play().catch((err) => {
    console.warn(`${warnLabel} sink play() rejected`, err);
  });
  return sink;
}
